import React, { useState, useEffect, useMemo } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout/legacy';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import './WhatsNewPage.css';
import defaultBanners from './banners.json';

const ResponsiveGridLayout = WidthProvider(Responsive);
const STORAGE_KEY = 'imatrix_whatsNew_banners';
const API_URL = ''; // Paste your Google Apps Script Web App URL here

const FILTER_TABS = [
  { id: 'all', label: '✦ All' },
  { id: 'offer', label: '🏷️ Offers' },
  { id: 'announcement', label: '📣 Announcements' },
  { id: 'card', label: '🆕 Updates' },
  { id: 'banner', label: '🖼️ Banners' },
];

const WhatsNewPage = () => {
  const [banners, setBanners] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    const load = async () => {
      if (API_URL) {
        try {
          const res = await fetch(API_URL);
          const data = await res.json();
          if (Array.isArray(data)) {
            setBanners(data);
            return;
          }
        } catch (err) {
          console.error("Error loading layout from Google Drive API:", err);
        }
      }
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const data = raw ? JSON.parse(raw) : defaultBanners;
        setBanners(Array.isArray(data) ? data : defaultBanners);
      } catch {
        setBanners(defaultBanners);
      }
    };
    load();
    window.addEventListener('whatsNewUpdated', load);
    window.addEventListener('storage', load);
    return () => {
      window.removeEventListener('whatsNewUpdated', load);
      window.removeEventListener('storage', load);
    };
  }, []);

  const visibleBanners = useMemo(() => {
    const now = new Date();
    return banners.filter(b => {
      if (!b.visible) return false;
      if (b.expiresAt && new Date(b.expiresAt) < now) return false;
      if (activeFilter !== 'all' && b.type !== activeFilter) return false;
      return true;
    });
  }, [banners, activeFilter]);

  const handleFilterChange = (id) => setActiveFilter(id);

  return (
    <div className="wn-page">
      <section className="wn-hero">
        <div className="wn-hero-inner">
          <span className="wn-hero-badge">Live Updates</span>
          <h1 className="wn-hero-title">What's <span className="wn-accent">New</span></h1>
          <p className="wn-hero-sub">Fresh offers, product updates, and important announcements.</p>
        </div>
        <div className="wn-hero-glow" aria-hidden="true" />
      </section>

      <div className="wn-filter-bar">
        <div className="wn-filter-inner">
          {FILTER_TABS.map(tab => (
            <button
              key={tab.id}
              className={`wn-filter-tab ${activeFilter === tab.id ? 'active' : ''}`}
              onClick={() => handleFilterChange(tab.id)}
            >
              {tab.label}
              {tab.id !== 'all' && (
                <span className="wn-tab-count">
                  {banners.filter(b => b.visible && b.type === tab.id).length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <main className="wn-grid-wrapper">
        {visibleBanners.length === 0 ? (
          <div className="wn-empty-state">
            <div className="wn-empty-icon">🌟</div>
            <h2>Nothing new yet</h2>
            <p>Check back soon — exciting updates are on the way!</p>
          </div>
        ) : (
          <ResponsiveGridLayout
            className="layout"
            layouts={{ lg: visibleBanners.map(b => ({ i: b.id, x: b.x || 0, y: b.y || 0, w: b.w || 4, h: b.h || 12 })) }}
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
            cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
            rowHeight={10}
            isDraggable={false}
            isResizable={false}
          >
            {visibleBanners.map(banner => (
              <div key={banner.id}>
                <BannerTile banner={banner} />
              </div>
            ))}
          </ResponsiveGridLayout>
        )}
      </main>
    </div>
  );
};

const BannerTile = ({ banner }) => {
  const hasImage = banner.imageUrl && banner.imageUrl.trim() !== '';
  const hasLink = banner.linkUrl && banner.linkUrl.trim() !== '';
  const layoutClass = `wn-layout-${banner.contentLayout || 'image-top'}`;

  let finalUrl = banner.linkUrl;
  if (finalUrl && !finalUrl.startsWith('http://') && !finalUrl.startsWith('https://') && !finalUrl.startsWith('/')) {
    finalUrl = 'https://' + finalUrl;
  }
  const isExternal = finalUrl && finalUrl.startsWith('http');

  const hasBadges = banner.pinned || banner.badge;
  const noImage = !hasImage || banner.contentLayout === 'text-only';

  const imgFit = banner.imageFit || 'contain';
  const imgSize = banner.imageSize !== undefined ? banner.imageSize : 50;

  const wrapStyle = {};
  const bodyStyle = {};

  if (hasImage && banner.contentLayout !== 'text-only') {
    if (banner.contentLayout === 'image-top') {
      wrapStyle.height = `${imgSize}%`;
      bodyStyle.height = `${100 - imgSize}%`;
      if (imgSize === 100) {
        bodyStyle.position = 'absolute';
        bodyStyle.bottom = 0;
        bodyStyle.left = 0;
        bodyStyle.right = 0;
        bodyStyle.height = 'auto';
        bodyStyle.background = 'rgba(0,0,0,0.7)';
        bodyStyle.color = '#ffffff';
        bodyStyle.backdropFilter = 'blur(8px)';
      }
    } else if (banner.contentLayout === 'image-left' || banner.contentLayout === 'image-right') {
      wrapStyle.width = `${imgSize}%`;
      bodyStyle.width = `${100 - imgSize}%`;
      if (imgSize === 100) {
        bodyStyle.position = 'absolute';
        bodyStyle.bottom = 0;
        bodyStyle.left = 0;
        bodyStyle.right = 0;
        bodyStyle.height = 'auto';
        bodyStyle.background = 'rgba(0,0,0,0.7)';
        bodyStyle.color = '#ffffff';
        bodyStyle.backdropFilter = 'blur(8px)';
      }
    }
  }

  return (
    <div
      className={`wn-tile ${layoutClass} ${banner.pinned ? 'wn-tile-pinned' : ''} ${noImage ? 'wn-tile-no-image' : ''} ${hasBadges ? 'wn-tile-has-badges' : ''}`}
      style={{
        background: banner.bgColor || 'var(--surface)',
        color: banner.textColor || 'var(--text-primary)',
        height: '100%'
      }}
    >
      <div className="wn-tile-badges">
        {banner.pinned && <span className="wn-pin-badge">📌 Pinned</span>}
        {banner.badge && (
          <span className="wn-custom-badge" style={{ background: banner.badgeColor || 'var(--brand)', color: '#fff' }}>
            {banner.badge}
          </span>
        )}
      </div>

      {hasImage && banner.contentLayout !== 'text-only' && (
        <div className="wn-tile-img-wrap" style={wrapStyle}>
          <img src={banner.imageUrl} alt="" className="wn-tile-img-blur" />
          <img src={banner.imageUrl} alt={banner.title} className="wn-tile-img" style={{ objectFit: imgFit }} />
        </div>
      )}

      <div className="wn-tile-body" style={bodyStyle}>
        {banner.title && <h3 className="wn-tile-title">{banner.title}</h3>}
        {banner.subtitle && <p className="wn-tile-subtitle">{banner.subtitle}</p>}
        {banner.description && <p className="wn-tile-desc">{banner.description}</p>}

        {hasLink && (
          <a
            href={finalUrl}
            target={isExternal ? '_blank' : '_self'}
            rel="noopener noreferrer"
            className="wn-tile-cta"
            onClick={e => e.stopPropagation()}
          >
            {banner.linkLabel || 'Learn More'} →
          </a>
        )}
      </div>

      {banner.expiresAt && (
        <div className="wn-expiry-tag">
          Expires {new Date(banner.expiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
        </div>
      )}
      <div className="wn-tile-shimmer" aria-hidden="true" />
    </div>
  );
};

export default WhatsNewPage;
